import {
  apiKey,
  instanceUrl,
  supabaseClient,
} from "@/lib/utils/client.supabase.conf"
import { UserIdentity, UserRecord } from "@/modules/users/users.types"
import { QueryClient } from "@tanstack/react-query"
import polyglotI18nProvider from "ra-i18n-polyglot"
import en from "ra-language-english"
import fr from "ra-language-french"
import { supabaseAuthProvider, supabaseDataProvider } from "ra-supabase-core"
import { SupabaseConfig } from "./types"

const translations = { en, fr }

export const i18nProvider = polyglotI18nProvider(
  locale => translations[locale as keyof typeof translations],
  "fr",
  [
    { locale: "en", name: "English" },
    { locale: "fr", name: "Français" },
  ]
)

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
  },
})

export const transformUserData = async (
  formData: UserRecord
): Promise<UserRecord> => {
  const { avatar, id, ...rest } = formData
  if (typeof avatar === "object" && avatar.rawFile instanceof File) {
    // const fileExt = avatar.title?.split('.').pop() || 'jpg'
    // const fileName = `${id}.${fileExt}`
    const filePath = `avatars/${id}`

    const { error: uploadError } = await supabaseClient.storage
      .from("images")
      .upload(filePath, avatar.rawFile, {
        upsert: true,
      })

    if (uploadError) {
      console.error("Upload error:", uploadError.message)
      throw new Error(`Failed to upload image: ${uploadError.message}`)
    }

    const { data: urlData } = supabaseClient.storage
      .from("images")
      .getPublicUrl(filePath)
    if (!urlData.publicUrl) throw new Error("Failed to get public URL")

    return {
      ...rest,
      id,
      avatar: { title: id, src: urlData.publicUrl },
    }
  }

  return formData
}

export const dataProvider = supabaseDataProvider({
  instanceUrl,
  apiKey,
  supabaseClient,
} as SupabaseConfig)

export const authProvider = supabaseAuthProvider(supabaseClient, {
  getIdentity: async (user: {
    id: string
    email?: string | undefined
  }): Promise<UserIdentity> => {
    type UserResponse = { data: UserRecord | null; error: Error | null }
    const { data, error } = (await supabaseClient
      .from("users")
      .select("id, first_name, last_name, avatar")
      .match({ id: user.id, email: user.email })
      .single()) as UserResponse
    if (!data || error) {
      throw Error()
    }
    return {
      id: data.id,
      email: user.email || "",
      fullName: `${data.first_name || ""} ${data.last_name || ""}`,
      avatar: data.avatar.src,
    }
  },
  getPermissions: async user => {
    type PermissionRow = { resource: string; action: string }
    type PermissionResponse = {
      data: PermissionRow[] | null
      error: Error | null
    }

    const { data: permissions, error } = (await supabaseClient
      .from("permissions")
      .select("resource, action")
      .eq("user_id", user.id)
      .eq("allowed", true)) as PermissionResponse

    if (error) {
      console.error("Permission fetch error:", error)
      throw new Error(error.message)
    }

    // Convert to format: { posts: ['list', 'edit'], users: ['list'] }
    return permissions?.reduce<Record<string, string[]>>(
      (acc, { resource, action }) => {
        acc[resource] ??= []
        acc[resource].push(action)
        return acc
      },
      {}
    )
  },

  /*
  canAccess: async (
    user: { id: string },
    resource: string,
    action: string
  ): Promise<boolean> => {
    type PermissionRow = { resource: string; action: string }
    type PermissionResponse = {
      data: PermissionRow[] | null
      error: Error | null
    }

    const { data: permissions, error } = (await supabaseClient
      .from('permissions')
      .select('resource, action')
      .eq('user_id', user.id)
      .eq('allowed', true)) as PermissionResponse

    if (error) return false

    const permissionMap = (permissions || []).reduce<Record<string, string[]>>(
      (acc, { resource: res, action: act }) => {
        acc[res] ??= []
        acc[res].push(act)
        return acc
      },
      {}
    )
    console.log(
      resource in permissionMap && permissionMap[resource].includes(action)
    )
    return resource in permissionMap && permissionMap[resource].includes(action)
  },
  */
})
